// assetPreserve — pure helper for non-destructive asset-cache loading.
// Kept dependency-free (no Vite/import.meta/window) so it is unit-testable.

// Decide a critical asset list's value after a fetch attempt.
//
//   fetched != null  → authoritative server response (incl. an empty []) — use it.
//   fetched == null  → the fetch ERRORED. Preserve the prior in-memory value
//                      ONLY when re-initing the same character (a transient AAM
//                      5xx must not wipe a character's materials); on a real
//                      character switch fall back to [] so one character's data
//                      never bleeds into another.
//
// This is the guard that stops a transient asset-manager error from silently
// emptying a character's materials/textures/mapping (CLAUDE.md: no silent
// fallback to fake/empty data — loud failure, never silent wipe).
export function resolvePreservedAssets(fetched, sameChar, prev) {
  if (fetched != null) return fetched
  return sameChar ? prev : []
}

// Decide whether a rig-DERIVED material mapping (slot list with NO material
// assignments) may be persisted to the server, and which mapping to use.
//
// deriveMappingFromRig only runs when an init read returned an EMPTY mapping,
// but that read can be wrong (a transient asset-manager hiccup). Persisting the
// assignment-less derived list then CLOBBERS the user's slot→material bindings
// (the "material assigned but didn't save" / white-after-sync bug). So we
// re-confirm against authoritative server state right before the destructive
// PUT: if the server already has ANY mapping, keep it and never persist the
// derived one. Only persist when the server genuinely has no mapping.
//
// `existing` is the freshly re-fetched server mapping (array) or null/undefined
// when it could not be confirmed — in which case we also refuse to persist.
export function reconcileDerivedMapping(derived, existing) {
  if (Array.isArray(existing) && existing.length > 0) {
    return { persist: false, mapping: existing }   // authoritative server mapping wins
  }
  if (!Array.isArray(existing)) {
    return { persist: false, mapping: derived }     // unconfirmed → display only, don't clobber
  }
  return { persist: true, mapping: derived }        // server empty → safe to persist
}
